import {
  useMemo,
  useReducer,
  useContext,
  createContext,
  useEffect,
  useCallback,
  useState,
} from 'react';

import { useAsync } from 'react-use';

import * as http from './http';

import {
  getCreationRequest,
  mergeStops,
  getAddJourneyErrorHandler,
  getAddRoutingErrorHandler,
  getHoverMapBasedOnRoutePlan,
  getShipperOrdersBasedOnCurrentStopsForIndex,
  ERRORS,
} from './utils';

import { events } from './events';

import reducer, {
  INIT_STATE,
  setClear,
  setStops,
  setMoveStop,
  setRemoveStop,
  setRoutePlan,
  setAddItineraryId,
  setUI,
  setJourneyForUpgrade,
  setError,
  setModifiedCost,
  setEditStop,
  setMapStopFilter,
} from './state';

const RoutingContext = createContext();

const allowedJourneyStatuses = ['IN_TRANSIT', 'ASSIGNED'];

const basePromise = () => () => Promise.resolve();

const RoutingContextProvider = ({ children, eventEmitter }) => {
  const [mutateAfterCreation, setMutateAfterCreation] = useState(basePromise);
  const [state, dispatch] = useReducer(reducer, INIT_STATE);
  const {
    stops: currentStops,
    routePlan,
    ui,
    itineraryIds,
    journeyForUpgrade,
    error,
    modifiedCost,
    mapStopFilter,
  } = state;

  const routePlanStops = useMemo(() => getCreationRequest({ stops: currentStops }), [currentStops]);
  const hoverMap = useMemo(() => getHoverMapBasedOnRoutePlan(routePlanStops), [routePlanStops]);

  const handleAddItinerary = useCallback(
    async ({ itineraryId, baseItineraryEndpoint = 'itineraries' }) => {
      dispatch(setUI('fetching', true));
      try {
        const itinerary = await http.fetchItinerary(itineraryId, baseItineraryEndpoint);

        const stopIds = itinerary.stops.map((stop) => stop.id);

        if (!Object.keys(itineraryIds).includes(String(itinerary.id))) {
          dispatch(setAddItineraryId(itinerary.id, stopIds));
          const stopsToAdd = mergeStops(currentStops, itinerary.stops);
          dispatch(setStops(stopsToAdd));
        }

        if (itinerary.upgrade_journey) {
          dispatch(setJourneyForUpgrade(itinerary.upgrade_journey));
        }
      } catch (err) {
        console.error(err);
      } finally {
        dispatch(setUI('fetching', false));
      }
    },
    [itineraryIds, currentStops]
  );

  const moveStop = useCallback((fromIndex, toIndex) => {
    dispatch(setMoveStop(fromIndex, toIndex));
  }, []);

  const removeStop = useCallback((index) => {
    dispatch(setRemoveStop(index));
  }, []);

  const modifyStopTimes = useCallback((stopTimes, index) => {
    dispatch(
      setEditStop(index, {
        time_from: stopTimes.timeFrom,
        time_to: stopTimes.timeTo,
      })
    );
  }, []);

  const handleCreateRoute = useCallback(async () => {
    try {
      dispatch(setUI('creating', true));
      const itinerary = await http.createItinerary({
        stops: routePlanStops,
        journey_id: journeyForUpgrade?.id,
        fixed_cost: modifiedCost,
      });
      await mutateAfterCreation();
      eventEmitter.emit(events.ITINERARY_CREATED, { itinerary });
      dispatch(setClear());
    } catch (err) {
      const addJourneyError = await getAddRoutingErrorHandler(err);
      dispatch(setError(addJourneyError));
      console.error(err);
    } finally {
      dispatch(setUI('creating', false));
    }
  }, [routePlanStops, eventEmitter, journeyForUpgrade, modifiedCost, mutateAfterCreation]);

  const handleCreateRouteAssigned = useCallback(async () => {
    try {
      dispatch(setUI('creating', true));
      await http.createRouteAssigned(journeyForUpgrade?.id, {
        stops: routePlanStops,
        fixed_cost: modifiedCost,
      });
      await mutateAfterCreation();
      dispatch(setClear());
    } catch (err) {
      const addJourneyError = await getAddRoutingErrorHandler(err);
      dispatch(setError(addJourneyError));
      console.error(err);
    } finally {
      dispatch(setUI('creating', false));
    }
  }, [routePlanStops, modifiedCost, journeyForUpgrade, mutateAfterCreation]);

  const clear = useCallback(() => {
    dispatch(setClear());
  }, []);

  const addJourney = useCallback(
    async (journeyId) => {
      try {
        dispatch(setUI('fetching', true));

        const journey = await http.fetchJourneyForRouting(journeyId);

        if (!allowedJourneyStatuses.includes(journey?.status)) {
          throw ERRORS.JUST_ASSIGNED_JOURNEYS;
        }

        const journeyStops = journey.stops.map((stop) => ({
          ...stop,
          id: `${stop.stop_type}-${stop.shipment_ids.join(',')}`,
          upgrade_journey_id: journey.id,
        }));

        const newStops = mergeStops(currentStops, journeyStops, {
          shouldPrepend: true,
        });

        dispatch(setStops(newStops));
        dispatch(setJourneyForUpgrade(journey));
      } catch (err) {
        console.error(err);
        const addJourneyError = await getAddJourneyErrorHandler(err);
        dispatch(setError(addJourneyError));
      } finally {
        dispatch(setUI('fetching', false));
      }
    },
    [currentStops]
  );

  const addError = useCallback((addedError) => {
    dispatch(setError(addedError));
  }, []);

  const clearError = useCallback(() => {
    if (!error) {
      return;
    }
    dispatch(setError(null));
  }, [error]);

  const openMap = useCallback(() => {
    dispatch(setUI('mapOpened', true));
  }, []);

  const closeMap = useCallback(() => {
    dispatch(setUI('mapOpened', false));
  }, []);

  const addModifiedCost = useCallback((newModifiedCost) => {
    dispatch(setModifiedCost(newModifiedCost));
  }, []);

  const setHoveredStop = useCallback((newHoveredStop) => {
    dispatch(setUI('hoveredStop', newHoveredStop));
  }, []);

  const setMapFilter = useCallback((newMapStopFilter) => {
    dispatch(setMapStopFilter(newMapStopFilter));
  }, []);

  const hoveredStop = ui.hoveredStop;

  const isStopHovered = useCallback(
    (index) => {
      const indexArray = (hoverMap || {})[hoveredStop] || [];
      return indexArray.includes(index);
    },
    [hoveredStop, hoverMap]
  );

  const getShipperOrdersForStopIndex = useCallback(
    (index) => {
      return getShipperOrdersBasedOnCurrentStopsForIndex({
        index,
        currentStops,
      });
    },
    [currentStops]
  );

  const routePlanState = useAsync(async () => {
    if (!routePlanStops?.length) {
      return null;
    }

    try {
      return await http.debouncedFetchRoutePlan({
        stops: routePlanStops,
        journey_id: journeyForUpgrade?.id,
        fixed_cost: modifiedCost,
      });
    } catch (err) {
      console.error(err);
      return null;
    }
  }, [routePlanStops, journeyForUpgrade, modifiedCost]);

  useEffect(() => {
    if (routePlanState.loading || routePlanState.error) {
      dispatch(setRoutePlan(null));
      return;
    }

    dispatch(setRoutePlan(routePlanState?.value));
  }, [routePlanState]);

  useEffect(() => {
    eventEmitter.on(events.ADD_ITINERARY, handleAddItinerary);
    eventEmitter.on(events.ADD_TESSA_ITINERARY, handleAddItinerary);
    return () => {
      eventEmitter.off(events.ADD_ITINERARY, handleAddItinerary);
      eventEmitter.off(events.ADD_TESSA_ITINERARY, handleAddItinerary);
    };
  });

  const value = {
    stops: currentStops,
    journeyForUpgrade,
    error,
    moveStop,
    removeStop,
    loading: ui.fetching || ui.creating,
    routePlan,
    itineraryIds,
    usedItineraryIds: Object.keys(itineraryIds),
    createRoute: handleCreateRoute,
    createRouteAssigned: handleCreateRouteAssigned,
    addJourney,
    addError,
    clearError,
    clear,
    mapOpened: ui.mapOpened,
    openMap,
    closeMap,
    addModifiedCost,
    modifiedCost,
    setHoveredStop,
    hoveredStop,
    hoverMap,
    isStopHovered,
    modifyStopTimes,
    getShipperOrdersForStopIndex,
    mapStopFilter,
    setMapFilter,
    setMutateAfterCreation,
  };

  return <RoutingContext.Provider value={value}>{children}</RoutingContext.Provider>;
};

const useRouting = () => {
  return useContext(RoutingContext);
};

export { RoutingContextProvider, events, useRouting, ERRORS };
